import { getPublicSiteUrl } from '../../config/siteConfig';
import { SITE_NAME } from '../../config/brand';

const StructuredData = ({ data }) => {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

export const generateArticleSchema = (article, language = 'en', canonicalPath = null) => {
  const siteUrl = getPublicSiteUrl();
  const slug = article.slug || article.title?.toLowerCase().replace(/\s+/g, '-');
  const path =
    canonicalPath ||
    (language === 'en' ? `/article/${slug}` : `/${language}/article/${slug}`);
  const articleUrl = path.startsWith('http')
    ? path
    : `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`;

  const publisherName = article.articleSchema?.publisher || SITE_NAME;
  const articleSection = article.articleSchema?.articleSection || article.category;

  const authorSlug = article.authorSlug;
  const authorNode = {
    '@type': 'Person',
    name: article.author,
    ...(authorSlug && { url: `${siteUrl}/author/${authorSlug}` }),
    ...(article.authorBio && { description: article.authorBio }),
    ...(article.authorAvatar && { image: article.authorAvatar }),
  };

  const keywordSource = [
    ...(Array.isArray(article.seo?.keywords) ? article.seo.keywords : []),
    article.seo?.focusKeyword,
  ]
    .map((k) => String(k || '').trim())
    .filter(Boolean);
  const keywords = [...new Set(keywordSource.map((k) => k.toLowerCase()))]
    .map((lower) => keywordSource.find((k) => k.toLowerCase() === lower))
    .filter(Boolean);

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: article.imageAlt
      ? { '@type': 'ImageObject', url: article.imageUrl, caption: article.imageAlt }
      : article.imageUrl,
    datePublished: article.date || article.publishedAt,
    dateModified: article.updatedAt || article.date || article.publishedAt,
    author: authorNode,
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    url: articleUrl,
    articleSection: articleSection,
    inLanguage: language,
    ...(keywords.length ? { keywords: keywords.join(', ') } : {}),
  };
};

export const generateWebSiteSchema = (description) => {
  const siteUrl = getPublicSiteUrl();

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: siteUrl,
    description,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};

export const generateOrganizationSchema = (description = 'Discover thoughtful articles on technology, design, lifestyle and more from passionate writers.') => {
  const siteUrl = getPublicSiteUrl();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description,
    sameAs: [
      'https://twitter.com/bloomwik',
      'https://facebook.com/bloomwik',
      'https://linkedin.com/company/bloomwik',
    ],
  };
};

export const generateBreadcrumbSchema = (items) => {
  const siteUrl = getPublicSiteUrl();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
};

export const generateFAQPageSchema = (faqs = [], pageUrl) => {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  const siteUrl = getPublicSiteUrl();
  const url = pageUrl?.startsWith('http') ? pageUrl : `${siteUrl}${pageUrl || ''}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question || faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer || faq.a || '',
      },
    })),
    ...(url && { url }),
  };
};

export const generateHowToSchema = (howTo, pageUrl) => {
  if (!howTo?.name || !Array.isArray(howTo.steps) || howTo.steps.length < 3) return null;
  const siteUrl = getPublicSiteUrl();
  const url = pageUrl?.startsWith('http') ? pageUrl : `${siteUrl}${pageUrl || ''}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: howTo.name,
    step: howTo.steps.map((text, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: text.slice(0, 120),
      text,
    })),
    ...(url && { url }),
  };
};

export const generateAuthorSchema = (author) => {
  const siteUrl = getPublicSiteUrl();
  const slug = author.slug || author.baseSlug || encodeURIComponent(author.name);

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: author.name,
    ...(author.bio && { description: author.bio }),
    ...(author.avatar && { image: author.avatar }),
    url: `${siteUrl}/author/${slug}`,
    ...(author.socialLinks && {
      sameAs: Object.values(author.socialLinks).filter(Boolean),
    }),
    ...(author.customLinks?.length && {
      subjectOf: author.customLinks.map((link) => ({
        '@type': 'WebPage',
        name: link.label || link.url,
        url: link.url,
      })),
    }),
  };
};

export const generateCategorySchema = (category, language = 'en', articleCount = 0) => {
  const siteUrl = getPublicSiteUrl();
  const slug = category.slug || category.name?.toLowerCase().replace(/\s+/g, '-');
  const categoryUrl = language === 'en' 
    ? `${siteUrl}/category/${slug}`
    : `${siteUrl}/${language}/category/${slug}`;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name,
    description: category.description || `Articles in the ${category.name} category`,
    url: categoryUrl,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: articleCount,
      itemListElement: []
    },
    inLanguage: language,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
  };
};

export default StructuredData;



